import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle, ChevronDown, ChevronRight } from "lucide-react";
import { useParams } from "react-router-dom";
import { formatISTTime } from "../utils/utils";
import { DELIVERY_TYPES } from "../constants/commonConstants";

export function OrderDetails() {
    const { id } = useParams();

    const BASE_URL = process.env.REACT_APP_API_URL;
    const token = localStorage.getItem("token");

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [invoiceLoading, setInvoiceLoading] = useState(false);
    const [expandedItemIds, setExpandedItemIds] = useState(new Set());

    const toggleExpanded = (itemId) => {
        setExpandedItemIds((prev) => {
            const next = new Set(prev);
            if (next.has(itemId)) {
                next.delete(itemId);
            } else {
                next.add(itemId);
            }
            return next;
        });
    };

    const fetchOrderDetails = async (orderId) => {
        try {
            setLoading(true);

            const res = await axios.get(
                `${BASE_URL}/order-management/${orderId}`,
                {
                    headers: { Authorization: token },
                }
            );

            setData(res.data?.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (id) fetchOrderDetails(id);
    }, [id]);

    const fetchInvoice = async () => {
        try {
            setInvoiceLoading(true);

            const response = await axios.get(
                `${BASE_URL}/orders/${id}/invoice`,
                {
                    headers: { Authorization: token },
                }
            );
            const invoiceUrl = response?.data?.data?.invoice_url;

            if (invoiceUrl) {
                window.open(invoiceUrl, "_blank", "noopener,noreferrer");
            }
        } catch (err) {
            console.error(err);
        } finally {
            setInvoiceLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const map = {
            DELIVERED: "bg-green-100 text-green-700",
            CANCELLED: "bg-gray-200 text-gray-700",
            REJECTED: "bg-red-100 text-red-700",
            PROCESSING: "bg-blue-100 text-blue-700",
            OUT_FOR_DELIVERY: "bg-yellow-100 text-yellow-700",
        };
        return map[status] || "bg-gray-100 text-gray-700";
    };

    if (loading) {
        return <div className="p-8 text-gray-400">Loading order...</div>;
    }

    if (!data) return null;

    const {
        order_no,
        order_source,
        current_status,
        created_at,
        total_amount,
        wallet_amount_used,
        delivery_charges,
        discount_amount,
        payment_status,
        items = [],
        user,
        delivery_address,
        coupon,
        status_logs = [],
        delivery_type,
        payment_method,
        delivered_at,
        reason,
        b2b_gstin
    } = data;

    return (
    <div className="p-6 max-w-7xl mx-auto">
    {/* HEADER */}
        <div className="flex justify-between items-center mb-6">
            <div>
                <h1 className="text-2xl font-semibold">
                    Order #{order_no}
                </h1>
                <p className="text-sm text-gray-500">
                    {new Date(created_at).toLocaleString()}
                </p>
            </div>

            <span
                className={`px-3 py-1 rounded-full text-sm ${getStatusBadge(
                    current_status
                )}`}
            >
                {current_status.replaceAll("_", " ")}
            </span>
        </div>

    {/* MAIN GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            
            {/* LEFT SIDE (70%) */}
            <div className="lg:col-span-8 space-y-6">
                
                {/* CUSTOMER + ADDRESS */}
                <div className="grid md:grid-cols-2 gap-6">
                    <Card title="Customer">
                        <p className="font-medium">{order_source === "OFFLINE" ? "Walk-in sale" : user?.name}</p>
                        <p className="text-sm text-gray-500">
                            {order_source === "OFFLINE" ? "No customer details recorded" : user?.phone_number}
                        </p>
                    </Card>

                    <Card title="Order Information">
                        <div className="space-y-1 text-sm">
                            <p><span className="text-gray-500">Order type:</span> {order_source === "OFFLINE" ? "Offline sale" : "Online order"}</p>
                            {order_source !== "OFFLINE" && <p><span className="text-gray-500">Delivery:</span> {delivery_type || "-"}</p>}
                            <p><span className="text-gray-500">Payment method:</span> {payment_method || "-"}</p>
                            {order_source !== "OFFLINE" && <p><span className="text-gray-500">Payment status:</span> {payment_status || "-"}</p>}
                            <p><span className="text-gray-500">Delivered:</span> {delivered_at ? formatISTTime(delivered_at) : "-"}</p>
                            <p><span className="text-gray-500">GSTIN:</span> {b2b_gstin || "-"}</p>
                            {reason && <p><span className="text-gray-500">Reason:</span> {reason}</p>}
                            <button
                                type="button"
                                onClick={fetchInvoice}
                                disabled={invoiceLoading}
                                className="text-blue-600 hover:underline disabled:text-gray-400"
                            >
                                {invoiceLoading ? "Loading invoice..." : "View invoice"}
                            </button>
                        </div>
                    </Card>

                    <Card title={order_source === "OFFLINE" ? "Fulfillment" : "Delivery Address"}>
                        {order_source === "OFFLINE" ? (
                            <p className="font-medium">Walk-in · delivered at point of sale</p>
                        ) : delivery_type === DELIVERY_TYPES.STORE_PICKUP ?
                            <p className="font-bold">
                                Store Pickup
                            </p> : <>
                            <p className="font-medium">
                                {delivery_address?.name}
                            </p>
                            <p className="text-sm text-gray-500">
                                {delivery_address?.house},{" "}
                                {delivery_address?.street}
                            </p>
                            <p className="text-sm text-gray-500">
                                {delivery_address?.city}
                            </p>
                            </>
                        }
                    </Card>
                </div>

                {/* ITEMS */}
                <div className="bg-white border rounded-xl p-5 shadow-sm">
                    <h3 className="font-medium mb-4">Items</h3>

                    <div className="divide-y">
                        {items.map((item) => {
                            const hasSubItems = item.sub_items?.length > 0;
                            const isExpanded = expandedItemIds.has(item.id);

                            return (
                                <div key={item.id}>
                                    <div
                                        className={`flex justify-between py-3 ${hasSubItems ? "cursor-pointer" : ""}`}
                                        onClick={hasSubItems ? () => toggleExpanded(item.id) : undefined}
                                    >
                                        <div className="flex gap-3">
                                            {hasSubItems && (
                                                isExpanded
                                                    ? <ChevronDown className="w-4 h-4 mt-1 text-gray-400 shrink-0" />
                                                    : <ChevronRight className="w-4 h-4 mt-1 text-gray-400 shrink-0" />
                                            )}

                                            <img
                                                src={item.image_url}
                                                className="w-12 h-12 rounded-lg object-cover"
                                            />

                                            <div>
                                                <p className="font-medium">
                                                    {item.name}
                                                    {hasSubItems && (
                                                        <span className="ml-2 text-xs font-normal text-purple-700 bg-purple-100 px-2 py-0.5 rounded">
                                                            Combo · {item.sub_items.length} items
                                                        </span>
                                                    )}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {item.unit_quantity} {item.base_unit || "kg"} ×{" "}
                                                    {item.quantity}
                                                </p>
                                            </div>
                                        </div>

                                        <p className="font-medium">
                                            ₹{(item.mrp * item.quantity).toFixed(2)}
                                        </p>
                                    </div>

                                    {hasSubItems && isExpanded && (
                                        <div className="pl-9 pb-3 space-y-2">
                                            {item.sub_items.map((subItem) => (
                                                <div
                                                    key={subItem.id}
                                                    className="flex justify-between items-center bg-gray-50 rounded-lg px-3 py-2"
                                                >
                                                    <div className="flex gap-3 items-center">
                                                        <img
                                                            src={subItem.image_url}
                                                            className="w-9 h-9 rounded-lg object-cover"
                                                        />

                                                        <div>
                                                            <p className="text-sm font-medium">
                                                                {subItem.name}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {subItem.unit_quantity} {subItem.base_unit || "kg"} ×{" "}
                                                                {subItem.quantity}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    <p className="text-sm text-gray-600">
                                                        ₹{(subItem.mrp * subItem.quantity).toFixed(2)}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* SUMMARY */}
                <div className="bg-white border rounded-xl p-5 shadow-sm">
                    <h3 className="font-medium mb-3">Summary</h3>

                    <Row label="Subtotal">
                        ₹{(Number(total_amount || 0) - Number(delivery_charges || 0) + Number(discount_amount || 0)).toFixed(2)}
                    </Row>
                    <Row label={`Discount ${coupon ? `(${coupon?.code})` : ""}`}>
                        -₹{Number(discount_amount || 0).toFixed(2)}
                    </Row>
                    <Row label="Delivery">
                        ₹{Number(delivery_charges || 0).toFixed(2)}
                    </Row>
                    {Number(wallet_amount_used || 0) > 0 && (
                        <Row label="Wallet Amount">
                            -₹{Number(wallet_amount_used).toFixed(2)}
                        </Row>
                    )}
                    <Row label="Order Total" bold>
                        ₹{Math.max(0, Number(total_amount || 0) - Number(wallet_amount_used || 0)).toFixed(2)}
                    </Row>
                    <Row label="Payment">
                        {order_source === "OFFLINE" ? "Paid" : payment_status || "-"}
                    </Row>
                </div>
            </div>

            {/* RIGHT SIDE (30%) */}
            <div className="lg:col-span-4">
                <div className="bg-white border rounded-xl p-5 shadow-sm sticky top-6">
                    <h3 className="font-medium mb-4">Order Timeline</h3>

                    {status_logs.length === 0 ? (
                        <p className="text-sm text-gray-500">
                            No updates yet
                        </p>
                    ) : (
                        <div className="space-y-5">
                            {status_logs.map((log, i) => (
                                <div key={log.id} className="flex gap-3">
                                    <CheckCircle className="text-blue-500 mt-1" />

                                    <div>
                                        <p className="text-sm font-medium">
                                            {log.status.replaceAll("_", " ")}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {formatISTTime(
                                                log.created_at
                                            )}
                                        </p>

                                        {log.remarks && (
                                            <p className="text-xs text-gray-500">
                                                {log.remarks}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    </div>
    );
}

/* UI helpers */

const Card = ({ title, children }) => (
    <div className="bg-white border rounded-xl p-5 shadow-sm">
        <h3 className="text-sm text-gray-500 mb-2">{title}</h3>
        {children}
    </div>
);

const Row = ({ label, children, bold }) => (
    <div className="flex justify-between text-sm py-1">
        <span className="text-gray-500">{label}</span>
        <span className={bold ? "font-semibold" : ""}>
            {children}
        </span>
    </div>
);